// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngSignIn from "@gcg/App/Components/XngSignIn/XngSignIn";
import XPopup from "@gcg/App/Components/XPopup/XPopup";
import { xHeaderState } from "@gcg/App/Store/Reducers";
import { screen } from "@gcg/XDL/Helpers";
import ChevronRight from "@gcg/XDL/Icons/ChevronRight";
import LogIn from "@gcg/XDL/Icons/LogIn";
import MyAccount from "@gcg/XDL/Icons/MyAccount";
import { color } from "@gcg/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import User from "@insite/mobius/Icons/User";
import { Hidden } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    links = "links",
    isGroup = "isGroup",
}

export interface CustomLinkModel {
    fields: {
        isGroup: boolean;
        destination: LinkFieldValue;
        title: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.links]: CustomLinkModel[];
    };
}

const getAllowedLinks = (state: ApplicationState, ownProps: OwnProps) =>
    (ownProps.fields.links || [])
        .map((linkItem: CustomLinkModel) => {
            const node = getPageLinkByNodeId(state, linkItem?.fields?.destination?.value);
            return {
                isGroup: linkItem.fields.isGroup,
                url: node?.url || "",
                excludeFromNavigation: node?.excludeFromNavigation,
                title: linkItem.fields.isGroup ? linkItem.fields.title : node?.title,
                pageType: node?.type,
            };
        })
        .filter(
            i =>
                ((!i.excludeFromNavigation || i.isGroup) && i.pageType !== "ChangeCustomerPage") ||
                (i.pageType === "ChangeCustomerPage" && state.context.session.displayChangeCustomerLink),
        );

const mapStateToProps = (state: ApplicationState & xHeaderState, ownProps: OwnProps) => {
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.firstName || state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        // customization
        isSticky: state.xHeader.isSticky,
        session: state.context.session,
        selectedBillToId: state.context.session.billToId,
        billToState: getBillToState(state, state.context.session.billToId),
        links: getAllowedLinks(state, ownProps),
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,

    // customization
    loadBillTo,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleClickableSignIn?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ButtonPresentationProps;

    // customization
    myAccountContainer: InjectableCss;
    myAccountHeader: InjectableCss;
    myAccountBody: InjectableCss;
    myAccountGroup: InjectableCss;
    myAccountLink: InjectableCss;
    myAccountFooter: InjectableCss;
    changeCustomerClickable?: ButtonPresentationProps;
    billToInformations: InjectableCss;
    hideOverlay?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    hideOverlay: {
        css: css`
            @media (max-width: 1024px) {
                opacity: 0;
            }
        `,
    },
    signOutWrapper: {
        css: css`
            display: inline-flex;
        `,
    },
    titleClickable: {
        css: css`
            // margin-left: 10px;
            display: flex;
            flex-direction: column;
        `,
    },
    titleClickableSignIn: {
        css: css`
            margin-left: 45px;
            display: flex;
            flex-direction: column;
        `,
    },
    titleIcon: {
        color: "text.main",
        css: css`
            margin-bottom: 7px;
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
            font-size: 14px;
            color: ${getColor("text.main")};
            ${screen("lte", "md")`display: none;`}
            ${(props: any) => props.sticky && `font-size:12px;`}
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        sizeVariant: "small",
    },

    // customization
    myAccountContainer: {
        css: css`
            width: 400px;
            min-width: 300px;
            ${screen("lte", "md")`width: auto;`}
        `,
    },
    myAccountHeader: {
        css: css`
            font-size: 20px;
            border-bottom: 1px solid ${color("common.ui2")};
            margin: 10px 15px;
            a span {
                color: ${color("text.main")} !important;
            }
        `,
    },
    myAccountBody: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    myAccountGroup: {
        css: css`
            font-size: 16px;
            border-bottom: 1px solid ${color("common.ui2")};
            margin: 20px 15px 10px;
            width: 100%;
        `,
    },
    myAccountLink: {
        css: css`
            padding: 2px 15px;
            width: 50%;
            font-size: 14px;
        `,
    },
    myAccountFooter: {
        css: css`
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            background: ${color("common.ui3")};
            margin-top: 20px;
            padding: 0 15px;

            button {
                margin: 17px 0;
                min-width: 150px;
            }
        `,
    },
    changeCustomerClickable: { sizeVariant: "small", variant: "secondary" },
    billToInformations: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 5px 15px;
            font-size: 14px;
            div:last-child {
                text-align: center;
                width: 40%;
            }
            img {
                max-width: 140px;
                max-height: 60px;
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;
let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    // custom states
    isSticky,
    links,
    session,
    selectedBillToId,
    billToState,
    changeCustomerUrl,
}: Props) => {
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    if (fields.icon === "User") {
        icon = LogIn;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = myAccountPageLink?.children;
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }

            e.preventDefault();
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In / Register");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const SignInElement = () => (
        <StyledWrapper>
            <Clickable {...styles.titleClickableSignIn} onClick={onSignInHandler} data-test-selector="header_signIn">
                {showIcon && <Icon {...styles.titleIcon} size={isSticky ? 24 : 30} src={LogIn} />}
                {showLabel && (
                    <Typography {...styles.titleTypography} {...({ sticky: isSticky } as any)}>
                        {signInStatusText}
                    </Typography>
                )}
            </Clickable>
        </StyledWrapper>
    );

    const UserElement = () => (
        <StyledWrapper>
            <Clickable
                {...styles.titleClickable}
                data-test-selector="header_signIn"
                onClick={() => history.push(myAccountPageLink?.url as string)}
            >
                {showIcon && <Icon {...styles.titleIcon} size={isSticky ? 24 : 30} src={MyAccount} />}
                {showLabel && (
                    <Typography
                        {...styles.titleTypography}
                        {...({ sticky: isSticky } as any)}
                        data-test-selector="header_userName"
                    >
                        {translate("Welcome")}, {userName}!
                    </Typography>
                )}
            </Clickable>
        </StyledWrapper>
    );

    const MyAccountOverlay = () => (
        <StyledWrapper {...styles.myAccountContainer}>
            <StyledWrapper {...styles.myAccountHeader}>
                <Link href={myAccountPageLink?.url}>{translate("My Account")}</Link>
            </StyledWrapper>
            <StyledWrapper {...styles.myAccountBody}>
                {links.map((link: any) =>
                    link?.isGroup ? (
                        <Typography as="div" key={uniqueId()} {...styles.myAccountGroup}>
                            {translate(link.title)}
                        </Typography>
                    ) : (
                        <Link key={uniqueId()} href={link.url} {...styles.myAccountLink}>
                            <Icon size={8} src={ChevronRight} color="primary" style={{ marginRight: "3px" }} />
                            {link.title}
                        </Link>
                    ),
                )}
            </StyledWrapper>

            <StyledWrapper {...styles.myAccountGroup} style={{ width: "auto" }}>
                {translate("Logged in as")}:
            </StyledWrapper>
            <StyledWrapper {...styles.billToInformations}>
                <div>
                    <p
                        style={{
                            margin: 0,
                            fontWeight: "bold",
                            fontSize: "16px",
                            lineHeight: "19px",
                            marginBottom: "10px",
                        }}
                    >
                        {billToState.value?.companyName}
                    </p>
                    {translate("Account #")}:{" "}
                    {billToState.value?.properties["soldTo"]}
                </div>
                <div>
                    {billToState.value?.properties["logoImagePath"] && (
                        <img
                            src={billToState.value?.properties["logoImagePath"]}
                            alt={billToState.value?.companyName}
                        />
                    )}
                </div>
            </StyledWrapper>
            <StyledWrapper {...styles.myAccountFooter}>
                <Button {...styles.signOutClickable} onClick={onSignOutHandler} data-test-selector="headerSignOutLink">
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {fields.includeAccountMenu && userName && !currentUserIsGuest && (
                <>
                    <Hidden below="md">
                        <XPopup
                            trigger={UserElement}
                            position="bottom center"
                            {...styles.hideOverlay}
                            customWidth={400}
                        >
                            <MyAccountOverlay />
                        </XPopup>
                    </Hidden>
                    <Hidden above="sm">
                        <UserElement />
                    </Hidden>
                </>
            )}
            {(!fields.includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <XPopup trigger={SignInElement} position="bottom center" {...styles.hideOverlay} customWidth={400}>
                    <XngSignIn />
                </XPopup>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In / MyAccount Links",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                isVisible: item => item.fields.includeAccountMenu,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;

                    if (item.fields[fields.isGroup]) {
                        return `Group Name: ${item.fields.title}`;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }

                    if (type === "Category") {
                        return value;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 4,
                fieldDefinitions: [
                    {
                        name: fields.isGroup,
                        displayName: "Is group",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "destination",
                        editorTemplate: "CustomLinkField" as any,
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: item => !item.fields[fields.isGroup],
                    },
                    {
                        name: "title",
                        displayName: "Groupe Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields[fields.isGroup] === true,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
